// Partners portal thunks.
//
// Every endpoint in backend/api/partners/ has a matching action here so
// screens can call dispatch(fetchPartner()) etc. without knowing about
// the URL shape. All requests go through apiPartnersGet/Post/Patch/Delete
// which target the partners service basePath; the public verify/lookup-npi/
// logo endpoints set noAuth so the JWT-refresh round-trip is skipped.

import {
  apiPartnersGet,
  apiPartnersPost,
  apiPartnersPatch,
  apiPartnersDelete,
  apiGet,
} from '../../utilities/apiClient';
import {
  SET_PARTNER,
  SET_PARTNER_FETCHING,
  SET_PARTNER_CUSTOMERS,
  SET_PARTNER_PROJECTS,
  SET_PARTNER_INVITES,
  UPSERT_PARTNER_INVITE,
  SET_PARTNER_POPULATION,
  SET_WHITELABEL_OVERRIDE,
} from '../constants';
import debug from '../../utilities/debug';
import { isWeb } from '../../utilities/platform';

const setFetching = (key: string, value: boolean) =>
  ({ type: SET_PARTNER_FETCHING, payload: { key, value } });

// Defensive dedupe for list-style payloads. The /partners/customers/list and
// /partners/projects/list endpoints already use DISTINCT ON (a.account_id) /
// (id), but staging deploys can lag behind the client and a not-yet-deployed
// backend will return rows duplicated by LEFT JOINs (multiple plans, multiple
// invites). Without this, FlatList logs "Encountered two children with the
// same key" because keyExtractor returns the same account_id twice. Dedupe
// here so the rest of the app sees a clean list regardless of backend state.
const dedupeBy = <T extends Record<string, any>>(
  items: T[],
  pickKey: (item: T) => string | number | undefined | null,
): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    const key = pickKey(item);
    if (key == null) { out.push(item); continue; }
    const k = String(key);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
};

// ─── /me ─────────────────────────────────────────────────────────────────────

export const fetchPartner =
  (accountId: string, cognitoId: string) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId) return null;
    dispatch(setFetching('partner', true));
    try {
      const res = await apiPartnersGet(`/me/${accountId}/${cognitoId}`);
      if (res?.success) {
        dispatch({
          type: SET_PARTNER,
          payload: { partner: res.payload?.partner, gates: res.payload?.gates },
        });
        return res.payload?.partner ?? null;
      }
      return null;
    } catch (err: any) {
      debug.error('fetchPartner failed', { err: err?.message });
      return null;
    } finally {
      dispatch(setFetching('partner', false));
    }
  };

export const updatePartnerSelf =
  (accountId: string, cognitoId: string, updates: Record<string, any>) =>
  async (dispatch: any): Promise<any | { error: { code?: string; message: string } } | null> => {
    if (!accountId || !cognitoId) return null;
    try {
      const res = await apiPartnersPatch(`/me/${accountId}/${cognitoId}`, updates);
      if (res?.success && res.payload?.partner) {
        // Refetch to pick up updated gates from /me, since PATCH only
        // returns the row (no derived gate block).
        dispatch(fetchPartner(accountId, cognitoId));
        return res.payload.partner;
      }
      // Structured error path — surfaces the server's errorCode +
      // message so the screen can render a specific alert
      // (e.g. logo_too_large gets its own copy instead of the generic
      // "Save failed").
      if (res && res.success === false) {
        return { error: { code: res.errorCode || null, message: res.message || 'Save failed.' } };
      }
      return null;
    } catch (err: any) {
      debug.error('updatePartnerSelf failed', { err: err?.message });
      return null;
    }
  };

// ─── Onboarding ──────────────────────────────────────────────────────────────

export const onboardPartner =
  (accountId: string, cognitoId: string, body: Record<string, any> = {}) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId) return null;
    try {
      const res = await apiPartnersPost(`/onboard/${accountId}/${cognitoId}`, body);
      if (res?.success && res.payload?.partner) {
        dispatch({
          type: SET_PARTNER,
          payload: {
            partner: res.payload.partner,
            // /onboard doesn't return gates; immediately follow-up with
            // /me to populate them. The screen can show "submitted!"
            // optimistically without waiting on this call.
            gates: null,
          },
        });
        dispatch(fetchPartner(accountId, cognitoId));
        return res.payload.partner;
      }
      return null;
    } catch (err: any) {
      debug.error('onboardPartner failed', { err: err?.message });
      return null;
    }
  };

// ─── Customers ───────────────────────────────────────────────────────────────

export type CustomerStatusFilter = 'active' | 'released' | 'all';

export const fetchPartnerCustomers =
  (accountId: string, cognitoId: string, status: CustomerStatusFilter = 'active') =>
  async (dispatch: any): Promise<any[]> => {
    if (!accountId || !cognitoId) return [];
    dispatch(setFetching('customers', true));
    try {
      const res = await apiPartnersGet(`/customers/${accountId}/${cognitoId}?status=${status}`);
      const raw: any[] = res?.success ? (res.payload?.customers ?? []) : [];
      const list = dedupeBy(raw, (c) => c?.account_id);
      dispatch({ type: SET_PARTNER_CUSTOMERS, payload: list });
      return list;
    } catch (err: any) {
      debug.error('fetchPartnerCustomers failed', { err: err?.message });
      return [];
    } finally {
      dispatch(setFetching('customers', false));
    }
  };

export const fetchPartnerCustomer =
  (accountId: string, cognitoId: string, customerCognitoId: string) =>
  async (): Promise<any | null> => {
    if (!accountId || !cognitoId || !customerCognitoId) return null;
    try {
      const res = await apiPartnersGet(`/customers/${customerCognitoId}/${accountId}/${cognitoId}`);
      return res?.success ? res.payload : null;
    } catch (err: any) {
      debug.error('fetchPartnerCustomer failed', { err: err?.message });
      return null;
    }
  };

export const releasePartnerCustomer =
  (accountId: string, cognitoId: string, customerCognitoId: string) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId || !customerCognitoId) return null;
    try {
      const res = await apiPartnersPost(
        `/customers/${customerCognitoId}/release/${accountId}/${cognitoId}`,
        {}
      );
      if (res?.success) {
        // Refresh the active list so the released customer drops out.
        dispatch(fetchPartnerCustomers(accountId, cognitoId, 'active'));
        return res.payload;
      }
      return null;
    } catch (err: any) {
      debug.error('releasePartnerCustomer failed', { err: err?.message });
      return null;
    }
  };

// ─── Projects ────────────────────────────────────────────────────────────────

export type ProjectStatusFilter = 'active' | 'released' | 'all';

export const fetchPartnerProjects =
  (
    accountId: string,
    cognitoId: string,
    status: ProjectStatusFilter = 'active',
    opts: { includeMembers?: boolean } = {},
  ) =>
  async (dispatch: any): Promise<any[]> => {
    if (!accountId || !cognitoId) return [];
    dispatch(setFetching('projects', true));
    try {
      // include_members attaches each plan's care-team roster (project_memberships
      // → users) so the Messages compose picker can DM any member. Off by default
      // so the Plans listing stays lean.
      const memberQs = opts.includeMembers ? '&include_members=true' : '';
      const res = await apiPartnersGet(`/projects/${accountId}/${cognitoId}?status=${status}${memberQs}`);
      const raw: any[] = res?.success ? (res.payload?.projects ?? []) : [];
      const list = dedupeBy(raw, (p) => p?.id ?? p?.project_id);
      // Stash on the partners slice so the Plans screen can render
      // without prop-drilling. Reusing the customers pattern keeps the
      // reducer changes minimal — we just need to add a new key.
      dispatch({ type: SET_PARTNER_PROJECTS, payload: list });
      return list;
    } catch (err: any) {
      debug.error('fetchPartnerProjects failed', { err: err?.message });
      return [];
    } finally {
      dispatch(setFetching('projects', false));
    }
  };

// ─── Invites ─────────────────────────────────────────────────────────────────

export const fetchPartnerInvites =
  (accountId: string, cognitoId: string) =>
  async (dispatch: any): Promise<any[]> => {
    if (!accountId || !cognitoId) return [];
    dispatch(setFetching('invites', true));
    try {
      const res = await apiPartnersGet(`/invites/${accountId}/${cognitoId}`);
      const raw: any[] = res?.success ? (res.payload?.invites ?? []) : [];
      const list = dedupeBy(raw, (i) => i?.id ?? i?.invite_code);
      dispatch({ type: SET_PARTNER_INVITES, payload: list });
      return list;
    } catch (err: any) {
      debug.error('fetchPartnerInvites failed', { err: err?.message });
      return [];
    } finally {
      dispatch(setFetching('invites', false));
    }
  };

export const sendPartnerInvites =
  (accountId: string, cognitoId: string, invites: any[], action: 'send' | 'resend' = 'send') =>
  async (dispatch: any): Promise<{ success: boolean; payload?: any; message?: string; errorCode?: string } | null> => {
    if (!accountId || !cognitoId || !Array.isArray(invites) || !invites.length) {
      return { success: false, message: 'Missing accountId / cognitoId / invites.' };
    }
    try {
      const res = await apiPartnersPost(
        `/invites/${accountId}/${cognitoId}`,
        { invites, action }
      );
      if (res?.success) {
        // Refresh to pick up the new / updated rows. The partner UI usually
        // displays counts by status so a full refetch is the simplest
        // honest representation.
        dispatch(fetchPartnerInvites(accountId, cognitoId));
        return { success: true, payload: res.payload };
      }
      // Surface backend errorCode + message so the screen can render the
      // exact reason (PARTNER_NOT_ACTIVE, MAX_ACCOUNTS, etc.) rather than
      // the previous generic "Please try again".
      debug.error('sendPartnerInvites server-error', { errorCode: res?.errorCode, message: res?.message });
      return { success: false, errorCode: res?.errorCode, message: res?.message || 'Send failed.' };
    } catch (err: any) {
      debug.error('sendPartnerInvites threw', { err: err?.message });
      return { success: false, message: err?.message || 'Network error.' };
    }
  };

export const updatePartnerInvite =
  (accountId: string, cognitoId: string, inviteId: string, patch: { invite?: Record<string, any>; overrides?: Record<string, any> }) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId || !inviteId) return null;
    try {
      const res = await apiPartnersPatch(
        `/invites/${inviteId}/${accountId}/${cognitoId}`,
        patch
      );
      if (res?.success && res.payload?.invite) {
        dispatch({ type: UPSERT_PARTNER_INVITE, payload: res.payload.invite });
        return res.payload.invite;
      }
      return null;
    } catch (err: any) {
      debug.error('updatePartnerInvite failed', { err: err?.message });
      return null;
    }
  };

export const revokePartnerInvite =
  (accountId: string, cognitoId: string, inviteId: string) =>
  async (dispatch: any): Promise<boolean> => {
    if (!accountId || !cognitoId || !inviteId) return false;
    try {
      const res = await apiPartnersDelete(
        `/invites/${inviteId}/${accountId}/${cognitoId}`
      );
      if (res?.success) {
        dispatch(fetchPartnerInvites(accountId, cognitoId));
        return true;
      }
      return false;
    } catch (err: any) {
      debug.error('revokePartnerInvite failed', { err: err?.message });
      return false;
    }
  };

// ─── Intakes ─────────────────────────────────────────────────────────────────

export const submitPartnerIntake =
  (accountId: string, cognitoId: string, data: Record<string, any>) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId) return null;
    try {
      const res = await apiPartnersPost(`/intakes/${accountId}/${cognitoId}`, { data });
      if (res?.success && res.payload?.intake) {
        dispatch({ type: UPSERT_PARTNER_INVITE, payload: res.payload.intake });
        return res.payload.intake;
      }
      return null;
    } catch (err: any) {
      debug.error('submitPartnerIntake failed', { err: err?.message });
      return null;
    }
  };

export const updatePartnerIntake =
  (accountId: string, cognitoId: string, intakeId: string, data: Record<string, any>) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId || !intakeId) return null;
    try {
      const res = await apiPartnersPatch(
        `/intakes/${intakeId}/${accountId}/${cognitoId}`,
        { data }
      );
      if (res?.success && res.payload?.intake) {
        dispatch({ type: UPSERT_PARTNER_INVITE, payload: res.payload.intake });
        return res.payload.intake;
      }
      return null;
    } catch (err: any) {
      debug.error('updatePartnerIntake failed', { err: err?.message });
      return null;
    }
  };

export const convertPartnerIntake =
  (accountId: string, cognitoId: string, intakeId: string, body: Record<string, any> = {}) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId || !intakeId) return null;
    try {
      const res = await apiPartnersPost(
        `/intakes/${intakeId}/convert/${accountId}/${cognitoId}`,
        body
      );
      if (res?.success) {
        // Convert produces a new account + family — refresh both invites
        // (the source intake is now status='redeemed') and customers (the
        // newly-created family appears).
        dispatch(fetchPartnerInvites(accountId, cognitoId));
        dispatch(fetchPartnerCustomers(accountId, cognitoId, 'active'));
        return res.payload;
      }
      return null;
    } catch (err: any) {
      debug.error('convertPartnerIntake failed', { err: err?.message });
      return null;
    }
  };

// ─── Population health ───────────────────────────────────────────────────────

export const fetchPartnerPopulationHealth =
  (accountId: string, cognitoId: string) =>
  async (dispatch: any): Promise<any | null> => {
    if (!accountId || !cognitoId) return null;
    dispatch(setFetching('populationHealth', true));
    try {
      const res = await apiPartnersGet(`/population-health/${accountId}/${cognitoId}`);
      if (res?.success) {
        const findings = dedupeBy(
          res.payload?.findings ?? [],
          (f: any) => f?.account_id ?? f?.id,
        );
        dispatch({
          type: SET_PARTNER_POPULATION,
          payload: {
            findings,
            lastUpdated: res.payload?.last_updated ?? null,
          },
        });
        return res.payload;
      }
      return null;
    } catch (err: any) {
      debug.error('fetchPartnerPopulationHealth failed', { err: err?.message });
      return null;
    } finally {
      dispatch(setFetching('populationHealth', false));
    }
  };

// ─── Public endpoints (no auth needed) ──────────────────────────────────────

export interface NPILookupBody {
  npi_number: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
}

export const lookupNPI = async (body: NPILookupBody): Promise<any | null> => {
  try {
    const res = await apiPartnersPost('/lookup-npi', body, { noAuth: true });
    return res?.success ? res : null;
  } catch (err: any) {
    debug.error('lookupNPI failed', { err: err?.message });
    return null;
  }
};

export const verifyReferralCode = async (code: string): Promise<any | null> => {
  if (!code) return null;
  try {
    const res = await apiPartnersGet(`/referral/verify/${encodeURIComponent(code)}`, { noAuth: true });
    return res?.success ? res.payload : null;
  } catch (err: any) {
    debug.error('verifyReferralCode failed', { err: err?.message });
    return null;
  }
};

// ---------------------------------------------------------------------------
// White-label subdomains (`<subdomain>.eleplan.com`)
//
// When the web app is served on a partner's subdomain, we resolve that host
// to the partner's branding (logo + colors) via the PUBLIC
// /partners/by-subdomain lookup and stash it as a pre-auth override so the
// login + register screens paint branded before the user signs in. Web-only:
// native builds have no host, and the apex/app hosts are reserved.
// ---------------------------------------------------------------------------

// Hosts that are the product itself, not a partner — short-circuit so a normal
// app.eleplan.com / www.eleplan.com load never fires the lookup. The server
// rejects these too (reserved-word blocklist), this just avoids the request.
const RESERVED_HOST_LABELS = new Set([
  'app', 'www', 'api', 'admin', 'beta', 'demo', 'next', 'cdn', 'dev',
  'development', 'staging', 'prod', 'production', 'eleplan',
]);
const ELEPLAN_ROOT = '.eleplan.com';

/**
 * Pull the partner subdomain label out of a host, or null if the host isn't a
 * partner subdomain (apex, app/www/api, a non-eleplan.com host, localhost, an
 * IP, or a multi-level host). Mirrors the server's slug rules: a single
 * lowercase [a-z0-9] label, ≤40 chars, not reserved.
 */
export const partnerSubdomainFromHost = (host?: string | null): string | null => {
  const h = String(host || '').toLowerCase().trim().replace(/:\d+$/, ''); // strip :port
  if (!h.endsWith(ELEPLAN_ROOT)) return null;
  const label = h.slice(0, -ELEPLAN_ROOT.length);
  if (!label || label.includes('.')) return null; // apex (eleplan.com) or multi-level
  if (RESERVED_HOST_LABELS.has(label)) return null;
  if (!/^[a-z0-9]{1,40}$/.test(label)) return null;
  return label;
};

/**
 * Boot-time hydration: detect a partner subdomain from the current host and,
 * if found, load that partner's public branding into the pre-auth override.
 * No-op on native and on non-partner hosts. Never throws — branding is a
 * progressive enhancement, so any failure just leaves the app unbranded.
 */
export const hydrateBrandingFromHost =
  () =>
  async (dispatch: any): Promise<void> => {
    try {
      if (!isWeb || typeof window === 'undefined') return;
      const sub = partnerSubdomainFromHost(window.location?.hostname);
      if (!sub) return;
      const res = await apiPartnersGet(`/by-subdomain/${encodeURIComponent(sub)}`, { noAuth: true });
      if (!res?.success || !res.payload) return;
      const b = res.payload;
      dispatch({
        type: SET_WHITELABEL_OVERRIDE,
        payload: {
          company_name: b.company_name ?? null,
          brand_primary: b.brand_primary ?? null,
          brand_secondary: b.brand_secondary ?? null,
          brand_logo: b.brand_logo ?? null,
          level: b.level ?? null,
        },
      });
    } catch (err: any) {
      debug.error('hydrateBrandingFromHost failed', { err: err?.message });
    }
  };

// Path-based sibling of hydrateBrandingFromHost for the co-branded partner
// landing page (/partners/<slug>). Resolves the slug to the partner's PUBLIC
// branding via the same /partners/by-subdomain lookup, stashes it as the
// pre-auth whitelabel override (so the landing page AND the register screen it
// hands off to both paint branded — useTheme + BrandLogo read the override),
// and returns the branding so the caller can fall back to neutral Eleplan copy
// when the slug doesn't resolve. noAuth — runs before the visitor has any
// session. Never throws.
export const hydrateBrandingFromSlug =
  (slug: string) =>
  async (dispatch: any): Promise<any | null> => {
    const s = String(slug || '').toLowerCase().trim();
    // Mirror the server's slug rule (a single [a-z0-9] label, <=40) so an
    // obviously invalid path never fires the lookup.
    if (!/^[a-z0-9]{1,40}$/.test(s)) return null;
    try {
      const res = await apiPartnersGet(`/by-subdomain/${encodeURIComponent(s)}`, { noAuth: true });
      if (!res?.success || !res.payload) return null;
      const b = res.payload;
      const override = {
        company_name: b.company_name ?? null,
        brand_primary: b.brand_primary ?? null,
        brand_secondary: b.brand_secondary ?? null,
        brand_logo: b.brand_logo ?? null,
        level: b.level ?? null,
      };
      dispatch({ type: SET_WHITELABEL_OVERRIDE, payload: override });
      return override;
    } catch (err: any) {
      debug.error('hydrateBrandingFromSlug failed', { slug: s, err: err?.message });
      return null;
    }
  };

export const linkReferralCode =
  (accountId: string, cognitoId: string, code: string) =>
  async (): Promise<any | null> => {
    if (!accountId || !cognitoId || !code) return null;
    try {
      const res = await apiPartnersPost(
        `/referral/link/${accountId}/${cognitoId}`,
        { code }
      );
      return res?.success ? res.payload : null;
    } catch (err: any) {
      debug.error('linkReferralCode failed', { err: err?.message });
      return null;
    }
  };

// ─── Partner-owned SurveyJS surveys ────────────────────────────────────────
//
// Some partners ship a custom intake survey (a "block" row with type='intake'
// and a SurveyJS-hosted survey_id). The web Intake page loads that survey
// instead of rendering its hand-built form; we mirror the same behavior on
// mobile via PartnerSurveyJSIntakeModal.
//
// The endpoint still lives on the legacy /client service (/blocks/get-
// partner-surveys) — the partners-service rebuild didn't touch it because
// blocks/surveys are project-scoped and reused across the partner +
// customer paths. apiGet() with the relative path hits the right service.
//
// Returns the raw blocks list — the caller picks the one with type='intake'.
export const fetchPartnerSurveys = async (
  accountId: string,
  cognitoId: string,
): Promise<any[]> => {
  if (!accountId || !cognitoId) return [];
  try {
    const res = await apiGet(`/blocks/get-partner-surveys/${accountId}/${cognitoId}`);
    return Array.isArray(res?.payload) ? res.payload : [];
  } catch (err: any) {
    debug.error('fetchPartnerSurveys failed', { err: err?.message });
    return [];
  }
};
